/**
 * useOnline — shared hook for online/offline detection.
 *
 * Two-layer strategy:
 *   1. `navigator.onLine` change events (cheap, immediate).
 *   2. Heartbeat to a lightweight backend endpoint — consecutive failures set
 *      `serverDown` (server problem), NOT `online=false` (device problem).
 *
 * Returns `{ online, serverDown, lastChangedAt }`. Never throws — safe to render into UI.
 */
import { useEffect, useState } from "react";

// `online` = the DEVICE has a network (navigator.onLine). `serverDown` = our API missed MISSES_FOR_DOWN
// heartbeats in a row while the device is online (deploy rollover, slow backend) — never shown as "no internet".
const MISSES_FOR_DOWN = 3;

export function useOnline({ heartbeatMs = 45_000, probePath = "/api/health" } = {}) {
  const initial = typeof navigator !== "undefined" ? navigator.onLine : true;
  const [online, setOnline] = useState(initial);
  const [serverDown, setServerDown] = useState(false);
  const [lastChangedAt, setLastChangedAt] = useState(() => new Date().toISOString());

  useEffect(() => {
    if (typeof window === "undefined") return;
    const flip = (next) => {
      setOnline(prev => {
        if (prev === next) return prev;
        setLastChangedAt(new Date().toISOString());
        return next;
      });
    };
    let killed = false;
    let misses = 0;
    let timer = null;
    const url = probePath.startsWith("http")
      ? probePath
      : (process.env.REACT_APP_BACKEND_URL || "") + probePath;
    const schedule = (ms) => { clearTimeout(timer); if (!killed) timer = setTimeout(heartbeat, ms); };
    const heartbeat = async () => {
      if (!navigator.onLine) { flip(false); return schedule(heartbeatMs); }
      flip(true);
      let ok = false;
      try {
        const ac = new AbortController();
        const t  = setTimeout(() => ac.abort(), 10000);
        const r  = await fetch(url, { method: "GET", cache: "no-store", signal: ac.signal });
        clearTimeout(t);
        ok = r.ok;
      } catch { ok = false; }
      misses = ok ? 0 : misses + 1;
      setServerDown(misses >= MISSES_FOR_DOWN);
      // While the server is missing, re-check sooner (5 s → 10 s → … ≤ heartbeat) so recovery shows fast.
      schedule(ok ? heartbeatMs : Math.min(heartbeatMs, 5000 * 2 ** Math.min(misses - 1, 3)));
    };
    const goOnline  = () => { flip(true); heartbeat(); };
    const goOffline = () => flip(false);
    window.addEventListener("online",  goOnline);
    window.addEventListener("offline", goOffline);
    heartbeat();
    return () => {
      killed = true;
      clearTimeout(timer);
      window.removeEventListener("online",  goOnline);
      window.removeEventListener("offline", goOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { online, serverDown, lastChangedAt };
}

/** Relative time helper — "3 min ago", "just now", "2 h ago". */
export function relativeTimeFrom(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 30)       return "just now";
  if (s < 90)       return "1 min ago";
  if (s < 3600)     return `${Math.round(s / 60)} min ago`;
  if (s < 86400)    return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}
